import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  nursingRecords: [],
  currentNursingRecord: null,
  nurses: [],
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all users with 'Nurse' role
export const fetchNurses = createAsyncThunk(
  'nursingRecords/fetchNurses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/roles/get-nurses');
      return response.data.data; // Backend se aane wala array
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Nurses'
      );
    }
  }
);

// Fetch all nursing records
export const fetchNursingRecords = createAsyncThunk(
  'nursingRecords/fetchNursingRecords',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/nursing-records/${patientId}`
        : '/nursing-records';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Nursing records'
      );
    }
  }
);

// Fetch single nursing record
export const fetchNursingRecord = createAsyncThunk(
  'nursingRecords/fetchNursingRecord',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/nursing-records/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Nursing record'
      );
    }
  }
);

// Create new nursing record
export const createNursingRecord = createAsyncThunk(
  'nursingRecords/createNursingRecord',
  async (recordData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/nursing-records`, recordData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Nursing record'
      );
    }
  }
);

// Update nursing record
export const updateNursingRecord = createAsyncThunk(
  'nursingRecords/updateNursingRecord',
  async ({ case_id, recordData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/nursing-records/case/${case_id}`, recordData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Nursing record'
      );
    }
  }
);

// Delete nursing record
export const deleteNursingRecord = createAsyncThunk(
  'nursingRecords/deleteNursingRecord',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/nursing-records/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Nursing record'
      );
    }
  }
);

// ==================== Slice ====================
const nursingRecordsSlice = createSlice({
  name: 'nursingRecords',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentNursingRecord: (state, action) => {
      state.currentNursingRecord = action.payload;
    },
    clearCurrentNursingRecord: (state) => {
      state.currentNursingRecord = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH NURSES (FOR DROPDOWN)
      .addCase(fetchNurses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNurses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nurses = action.payload; // Nurses list store ho gayi
      })
      .addCase(fetchNurses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // FETCH ALL
      .addCase(fetchNursingRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNursingRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingRecords = action.payload;
      })
      .addCase(fetchNursingRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchNursingRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNursingRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentNursingRecord = action.payload;
      })
      .addCase(fetchNursingRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createNursingRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createNursingRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingRecords.unshift(action.payload);
      })
      .addCase(createNursingRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateNursingRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNursingRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.nursingRecords.findIndex((d) => d.id === updated.id);
        if (index !== -1) state.nursingRecords[index] = updated;

        if (state.currentNursingRecord?.id === updated.id) {
          state.currentNursingRecord = updated;
        }
      })
      .addCase(updateNursingRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteNursingRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteNursingRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nursingRecords = state.nursingRecords.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentNursingRecord?.id === action.payload) {
          state.currentNursingRecord = null;
        }
      })
      .addCase(deleteNursingRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentNursingRecord, clearCurrentNursingRecord } =
  nursingRecordsSlice.actions;
export default nursingRecordsSlice.reducer;
